import * as React from 'react';
import {useParams} from 'react-router-dom';
import type {TablePaginationProps} from "@mui/material";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  useAddTargetListEmailMutation,
  useDeleteTargetEmailMutation,
  useGetEmailListDetailQuery,
  useUpdateEmailListNameMutation,
  useUpdateEmailStatusMutation
} from "../../../services/emailListApi";
import type {EmailContacts} from "../../../types/emailList";
import {toast} from "react-toastify";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import MoreVertIcon from "@mui/icons-material/MoreVert";

type EmailStatus = 'subscribed' | 'unsubscribed';
type FilterStatus = 'all' | EmailStatus;

interface RowMenuState {
  anchorEl: HTMLElement | null;
  row: EmailContacts | null;
}

const ActionsMenu = React.memo(function ActionsMenu({
                       state,
                       onClose,
                     }: {
  state: RowMenuState;
  onClose: (action?: string) => void;
}) {
  const open = Boolean(state.anchorEl);

  return (
    <Menu
      anchorEl={state.anchorEl}
      open={open}
      onClose={() => onClose()}
      anchorOrigin={{vertical: "bottom", horizontal: "right"}}
      transformOrigin={{vertical: "top", horizontal: "right"}}
    >
      <MenuItem onClick={() => onClose("subscribe")}>
        <ContentCopyIcon fontSize="small" sx={{mr: 1}}/>
        Subscribe
      </MenuItem>

      <MenuItem onClick={() => onClose("unsubscribe")}>
        <FileUploadIcon fontSize="small" sx={{mr: 1}}/>
        Unsubscribe
      </MenuItem>
      <Divider component="li" sx={{my: 0.5}}/>

      <MenuItem onClick={() => onClose("delete")} sx={{color: "error.main"}}>
        <DeleteForeverIcon fontSize="small" sx={{mr: 1}}/>
        Delete
      </MenuItem>
    </Menu>
  );
});

const EmailRow = React.memo(function EmailRow({
  email,
  onOpenMenu,
}: {
  email: EmailContacts;
  onOpenMenu: (event: React.MouseEvent<HTMLButtonElement>, row: EmailContacts) => void;
}) {
  return (
    <TableRow key={email.id} hover>
      <TableCell padding="checkbox">
        <Checkbox/>
      </TableCell>
      <TableCell>{email.email}</TableCell>
      <TableCell>{email.subscribed ? "subscribed" : "unsubscribed"}</TableCell>
      <TableCell align="right" sx={{width: 72}}>
        <IconButton onClick={(e) => onOpenMenu(e, email)} aria-label="row actions">
          <MoreVertIcon/>
        </IconButton>
      </TableCell>
    </TableRow>
  );
});

type PaginationActionsProps = {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: TablePaginationProps["onPageChange"];
};

const PaginationActions = React.memo(({
                             count,
                             page,
                             rowsPerPage,
                             onPageChange,
                           }: PaginationActionsProps) => {
  const totalPages = React.useMemo(() => Math.max(1, Math.ceil(count / rowsPerPage)), [count, rowsPerPage]);

  const handlePrev = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange?.(event, Math.max(0, page - 1));
    },
    [onPageChange, page]
  );

  const handleNext = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange?.(event, Math.min(totalPages - 1, page + 1));
    },
    [onPageChange, page, totalPages]
  );

  return (
    <Box sx={{display: "flex", alignItems: "center", gap: 1, ml: 2}}>
      <Select
        size="small"
        value={page}
        onChange={(e) =>
          onPageChange?.(null as unknown as React.MouseEvent<HTMLButtonElement>, Number(e.target.value))
        }
        sx={{minWidth: 72}}
      >
        {Array.from({length: totalPages}, (_, i) => (
          <MenuItem key={i} value={i}>
            {i + 1}
          </MenuItem>
        ))}
      </Select>

      <Typography variant="body2">of {totalPages} pages</Typography>

      <IconButton onClick={handlePrev} disabled={page === 0}>
        <KeyboardArrowLeftIcon/>
      </IconButton>

      <IconButton onClick={handleNext} disabled={page >= totalPages - 1}>
        <KeyboardArrowRightIcon/>
      </IconButton>
    </Box>
  );
});

export default function EditTargetEmailList() {
  const {id} = useParams<{ id: string }>();
  if (!id) {
    throw new Error("id is undefined");
  }

  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [newEmail, setNewEmail] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [emailFilter, setEmailFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<FilterStatus>('all');
  const [emailListName, setEmailListName] = React.useState('');
  const [menu, setMenu] = React.useState<RowMenuState>({anchorEl: null, row: null});

  React.useEffect(() => {
    const handler = window.setTimeout(() => {
      setEmailFilter(searchTerm);
    }, 300);

    return () => window.clearTimeout(handler);
  }, [searchTerm]);

  const queryArgs = React.useMemo(
    () => ({id, page, limit: rowsPerPage, query: emailFilter, status: statusFilter}),
    [id, page, rowsPerPage, emailFilter, statusFilter]
  );

  const {
    data: emailListDetail,
    isLoading,
    isFetching,
  } = useGetEmailListDetailQuery(queryArgs);

  const [updateEmailListApi, {isLoading: isUpdatingName}] = useUpdateEmailListNameMutation();
  const [addTargetListEmailApi, {isLoading: isAddingEmail}] = useAddTargetListEmailMutation();
  const [updateEmailStatusApi, {isLoading: isUpdateingStatus}] = useUpdateEmailStatusMutation();
  const [deleteTargetEmailApi, {isLoading: isDeletingEmail}] = useDeleteTargetEmailMutation();
  React.useEffect(() => {
    if (emailListDetail?.title) {
      setEmailListName(emailListDetail.title);
    }
  }, [emailListDetail?.title]);

  const handleUpdateName = React.useCallback(async () => {
    try {
      await updateEmailListApi({id, title: emailListName}).unwrap();
      toast.success("Email list name updated");
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || String(error));
    }
  }, [emailListName, id, updateEmailListApi]);

  const handleAddEmail = React.useCallback(async () => {
    if (!newEmail.trim()) {
      toast.error("Email is required");
      return;
    }

    try {
      await addTargetListEmailApi({email: newEmail.trim(), id}).unwrap();
      toast.success("Email added");
      setNewEmail('');
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || String(error));
    }
  }, [addTargetListEmailApi, id, newEmail]);

  const openMenu = React.useCallback((event: React.MouseEvent<HTMLButtonElement>, row: EmailContacts) => {
    setMenu({anchorEl: event.currentTarget, row});
  }, []);

  const closeMenu = React.useCallback(async (action?: string) => {
    if (action && menu.row) {
      try {
        switch (action.toLowerCase()) {
          case "delete":
            await deleteTargetEmailApi(menu.row.id).unwrap();
            break;
          case "unsubscribe":
            await updateEmailStatusApi({id: menu.row.id, subscribed: false}).unwrap();
            break;
          case "subscribe":
            await updateEmailStatusApi({id: menu.row.id, subscribed: true}).unwrap();
            break;
          default:
            break;
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Failed to perform action");
      }
    }

    setMenu({anchorEl: null, row: null});
  }, [deleteTargetEmailApi, menu.row, updateEmailStatusApi]);

  const handleChangePage = React.useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5">
        Edit target email list {emailListName}
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack spacing={3}>
          <Stack
            direction={{xs: 'column', md: 'row'}}
            spacing={2}
            alignItems={{xs: 'stretch', md: 'center'}}
          >
            <TextField
              required
              fullWidth
              label="Target email list"
              placeholder="e.g. Newsletter Subscribers"
              value={emailListName}
              onChange={(e) => setEmailListName(e.target.value)}
            />

            <Button
              variant="contained"
              onClick={handleUpdateName}
              disabled={isUpdatingName || !emailListName.trim()}
              sx={{minWidth: 140, height: 56, whiteSpace: 'nowrap'}}
            >
              Update
            </Button>
          </Stack>

          <Stack
            direction={{xs: 'column', md: 'row'}}
            spacing={2}
            alignItems={{xs: 'stretch', md: 'center'}}
          >
            <TextField
              required
              fullWidth
              label="Email"
              placeholder="e.g. user@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />

            <Button
              variant="contained"
              onClick={handleAddEmail}
              disabled={isAddingEmail || !newEmail.trim()}
              sx={{minWidth: 140, height: 56, whiteSpace: 'nowrap'}}
            >
              Add email
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack
          direction={{xs: 'column', md: 'row'}}
          spacing={2}
          alignItems={{xs: 'stretch', md: 'center'}}
        >
          <TextField
            label="Filter by email"
            placeholder="Type email..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            fullWidth
          />

          <FormControl sx={{minWidth: 220}}>
            <InputLabel id="email-status-filter-label">Status</InputLabel>
            <Select
              labelId="email-status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value as FilterStatus);
                setPage(0);
              }}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="subscribed">Subscribed</MenuItem>
              <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >

        <TablePagination
          component="div"
          count={emailListDetail?.totalCount ?? 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows per page"
          labelDisplayedRows={({from, to, count}) => `${from}-${to} of ${count}`}
          ActionsComponent={PaginationActions}

        />


      </Box>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox/>
              </TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {emailListDetail?.contacts.map((email) => (
              <EmailRow key={email.id} email={email} onOpenMenu={openMenu} />
            ))}

            {(emailListDetail?.contacts === undefined || emailListDetail?.contacts.length === 0) && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {isFetching ? "Loading..." : "No emails found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ActionsMenu state={menu} onClose={closeMenu}/>
    </Stack>
  );
}